/**
 * Centralized error tracking service
 * Integrates Sentry and PostHog for comprehensive error monitoring
 */

import * as Sentry from '@sentry/nextjs';

interface ErrorContext {
  userId?: string;
  organizationId?: string;
  requestId?: string;
  flowId?: string;
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
}

interface ErrorTrackingService {
  captureException: (error: Error, context?: ErrorContext) => void;
  captureMessage: (message: string, level?: 'info' | 'warning' | 'error') => void;
}

class ErrorTracker implements ErrorTrackingService {
  private tags: Record<string, string> = {};
  private extra: Record<string, unknown> = {};

  /**
   * Capture and log exceptions to both Sentry and PostHog
   */
  captureException(error: Error, context?: ErrorContext): void {
    const errorTags: Record<string, string> = { ...this.tags, ...context?.tags };
    if (context?.requestId) {
      errorTags['request_id'] = context.requestId;
    }
    if (context?.flowId) {
      errorTags['flow_id'] = context.flowId;
    }

    const errorData = {
      error,
      context: {
        ...context,
        tags: errorTags,
        extra: { ...this.extra, ...context?.extra },
      },
      timestamp: new Date().toISOString(),
    };

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error captured:', errorData);
    } else {
      // Capture in PostHog (client-only)
      if (typeof window !== 'undefined') {
        // Dynamic import to avoid SSR window reference
        import('posthog-js')
          .then(({ default: ph }) => {
            const client = ph as unknown as {
              captureException?: (
                error: Error,
                context?: {
                  tags?: Record<string, string>;
                  extra?: Record<string, unknown>;
                },
              ) => void;
              capture: (event: string, properties?: Record<string, unknown>) => void;
            };
            if (client.captureException) {
              client.captureException(error, {
                tags: errorData.context.tags,
                extra: errorData.context.extra,
              });
            } else {
              client.capture('exception', {
                message: error.message,
                stack: error.stack,
                ...errorData.context.tags,
                ...errorData.context.extra,
              });
            }
          })
          .catch(() => {
            // noop – PostHog unavailable client-side
          });
      }

      // Capture in Sentry
      Sentry.captureException(error, {
        tags: errorData.context.tags,
        extra: errorData.context.extra,
      });
    }
  }

  /**
   * Capture custom messages with specified log levels
   */
  captureMessage(message: string, level: 'info' | 'warning' | 'error' = 'info'): void {
    const messageData = {
      message,
      level,
      context: {
        tags: this.tags,
        extra: this.extra,
      },
    };

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`[${level.toUpperCase()}] ${message}`, messageData);
    } else {
      // Capture in PostHog (client-only)
      if (typeof window !== 'undefined') {
        import('posthog-js')
          .then(({ default: posthog }) => {
            posthog.capture('log_message', {
              level: level,
              message: message,
            });
          })
          .catch(() => {
            // noop – PostHog unavailable client-side
          });
      }

      // Capture in Sentry
      Sentry.captureMessage(message, {
        level: level as Sentry.SeverityLevel,
        tags: messageData.context?.tags,
        extra: messageData.context?.extra,
      });
    }
  }
}

// Export singleton instance
export const errorTracker = new ErrorTracker();

// Export the interface for type safety
export type { ErrorTrackingService };
